/**
 * FlexibleSectionContentAdapter — Bridges V2 section editors ↔ LayoutSection
 *
 * Creates a synthetic FlowStepNode (proxy step) whose content is mapped from
 * the section's content field, then intercepts the V2 editor's onChange(path, value)
 * calls and translates them back to updates on the section within the sections array.
 *
 * Path translation (example — Text kind):
 *   FlexibleTextSection writes: onChange('content.copy.title.translations.en', 'Hello')
 *   Adapter strips 'content.' → 'copy.title.translations.en'
 *   Maps prefix 'copy' → sectionField 'textContent', innerPath 'title.translations.en'
 *   Deep-clones section.textContent, applies setNestedValue, writes whole sections array.
 *
 * Safety: NEVER uses deep array-index paths through setNestedValue (creates {} not []).
 *   All writes go to content.flexibleLayout.sections (whole array replacement).
 *   The fieldCopy is always deep-cloned so setNestedValue cannot mutate shared references.
 *
 * Dispatches to:
 *   Text          → FlexibleTextSection (title only)
 *   Media         → FlexibleMediaSection (media + gradient)
 *   OptionsList   → V2OptionsListSection
 *   CustomEmbed   → V2EmbedSection
 *   ProductCatalog→ V2PaywallSubscriptionsSection
 *   Button        → FlexibleButtonSection / FlexiblePurchaseButtonSection
 */

import React, { useCallback, useMemo, useRef } from 'react';
import {
  setNestedValue,
  deleteNestedValue,
  routeTypographyOverridePath,
  pruneEmptyPresentationOverrides,
} from '@modules/reactFlow/utils/v2FieldOperations';
import { useEditorPresentationProfile } from './PresentationProfileContext';
import { FlexibleTextSection } from './FlexibleTextSection';
import { FlexibleButtonSection } from './FlexibleButtonSection';
import { FlexiblePurchaseButtonSection } from './FlexiblePurchaseButtonSection';
import { FlexibleMediaSection } from './FlexibleMediaSection';
import { V2OptionsListSection } from './V2OptionsListSection';
import { V2EmbedSection } from './V2EmbedSection';
import { V2PaywallSubscriptionsSection } from './V2PaywallSubscriptionsSection';
import { V2PaywallHeaderSection } from './V2PaywallHeaderSection';
import { V2TextFieldSection } from './V2TextFieldSection';
import { EmbedChildSectionsManager } from './EmbedChildSectionsManager';
import type { LayoutSection, SectionKind } from '@segmently/schema-models/v2';
import type { FlowStepNode } from '@modules/reactFlow/adapters/V2ScreenAdapter';

// ─── Path translation config ────────────────────────────────────────────────

interface KindContentConfig {
  /**
   * Prefix that the V2 editor writes to, after stripping 'content.' from the path.
   * e.g. 'copy' for CopyBlockSection, 'paywall.products' for PaywallSubscriptions.
   */
  onChangePrefixInContent: string;
  /** Field name on LayoutSection that stores this section's content. */
  sectionField: string;
  /**
   * Build the proxy FlowStepNode's content object from the section.
   * Must match exactly what the V2 editor expects to read.
   */
  buildProxyContent: (s: LayoutSection) => Record<string, unknown>;
  /**
   * ScreenType for the content editor proxy. Affects V2 editor behaviour:
   * - CopyBlockSection uses screenType to show/hide hero image upload
   * - OptionsListSection uses screenType to get validItemLayouts
   */
  proxyScreenType: string;
  /**
   * ScreenType override for variable binding proxy (buildBindingProxyStep).
   * When set, used instead of proxyScreenType so that getCompatibleTypes()
   * returns the correct variable types (e.g. ListMultiPick → enum[]).
   */
  bindingScreenType?: string;
}

const KIND_CONFIG: Record<string, KindContentConfig> = {
  Text: {
    onChangePrefixInContent: 'copy',
    sectionField: 'textContent',
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    buildProxyContent: (s) => ({ copy: (s as any).textContent }),
    proxyScreenType: 'TextInput', // TextInput hides hero image in V2CopyBlockSection
  },
  Media: {
    onChangePrefixInContent: 'featuredMedia',
    sectionField: 'mediaContent',
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    buildProxyContent: (s) => ({ featuredMedia: (s as any).mediaContent }),
    proxyScreenType: 'HeroContent',
  },
  OptionsList: {
    onChangePrefixInContent: 'options',
    sectionField: 'optionsListContent',
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    buildProxyContent: (s) => ({ options: (s as any).optionsListContent }),
    proxyScreenType: 'ListSinglePick', // Provides a sensible validItemLayouts set
  },
  SingleSelectionList: {
    onChangePrefixInContent: 'options',
    sectionField: 'optionsListContent',
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    buildProxyContent: (s) => ({ options: (s as any).optionsListContent }),
    proxyScreenType: 'ListSinglePick',
  },
  MultipleSelectionList: {
    onChangePrefixInContent: 'options',
    sectionField: 'optionsListContent',
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    buildProxyContent: (s) => ({ options: (s as any).optionsListContent }),
    // ListSinglePick has itemsHaveActions: true → Column Layout dropdown visible.
    // Item layout filtering uses getValidItemLayoutsForContext(columnLayout, 'many').
    proxyScreenType: 'ListSinglePick',
    // Variable binding needs ListMultiPick so getCompatibleTypes returns enum[]
    bindingScreenType: 'ListMultiPick',
  },
  BulletList: {
    onChangePrefixInContent: 'options',
    sectionField: 'optionsListContent',
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    buildProxyContent: (s) => ({ options: (s as any).optionsListContent }),
    proxyScreenType: 'HeroList', // HeroList hides selection/column dropdowns
  },
  CustomEmbed: {
    onChangePrefixInContent: 'embed',
    sectionField: 'embedContent',
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    buildProxyContent: (s) => ({ embed: (s as any).embedContent }),
    proxyScreenType: 'WebEmbed',
  },
  ProductCatalog: {
    // V2PaywallSubscriptionsSection reads/writes content.paywall.products.*
    onChangePrefixInContent: 'paywall.products',
    sectionField: 'productCatalogContent',
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    buildProxyContent: (s) => ({ paywall: { products: (s as any).productCatalogContent } }),
    proxyScreenType: 'Paywall',
  },
  Button: {
    onChangePrefixInContent: 'buttonContent',
    sectionField: 'buttonContent',
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    buildProxyContent: (s) => ({ buttonContent: (s as any).buttonContent }),
    proxyScreenType: 'TextInput',
  },
  PaywallHeader: {
    onChangePrefixInContent: 'paywall',
    sectionField: 'paywallHeaderContent',
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    buildProxyContent: (s) => ({ paywall: (s as any).paywallHeaderContent }),
    proxyScreenType: 'Paywall',
  },
  TextField: {
    onChangePrefixInContent: 'textField',
    sectionField: 'textFieldContent',
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    buildProxyContent: (s) => ({ textField: (s as any).textFieldContent }),
    proxyScreenType: 'TextInput',
  },
};

// ─── Proxy step builder for variable binding ─────────────────────────────────

/**
 * Build a proxy FlowStepNode for variable binding editors.
 * Sets correct screenType + content from KIND_CONFIG so V2VariableBindingSection
 * shows the "Variable Options" mapping table (requiresValueMapping + items).
 *
 * Uses bindingScreenType (when defined) instead of proxyScreenType so that
 * getCompatibleTypes() returns the correct variable types for the section kind
 * (e.g. MultipleSelectionList → ListMultiPick → enum[]).
 *
 * IMPORTANT: entityId is kept as baseStep.id (the SCREEN ID), NOT the section ID.
 * The section ID is passed separately via V2VariableBindingSection's `sectionId` prop,
 * which saves it as FunnelVariable.boundSectionId for runtime filtering.
 */
export function buildBindingProxyStep(
  baseStep: FlowStepNode,
  section: LayoutSection,
): FlowStepNode {
  const config = KIND_CONFIG[section.kind as string];
  if (!config) return baseStep;
  return {
    ...baseStep,
    screenType: (config.bindingScreenType ?? config.proxyScreenType) as FlowStepNode['screenType'],
    content: config.buildProxyContent(section) as unknown as FlowStepNode['content'],
  };
}

// ─── Purchase button detection ──────────────────────────────────────────────

/** Detect purchase buttons by action.kind === 'purchase' on the TapTarget */
function isPurchaseButton(section: LayoutSection): boolean {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  return (section as any).buttonContent?.action?.kind === 'purchase';
}

function buildAvailableParentSections(
  parentPool: LayoutSection[],
  section: LayoutSection,
  linkableKinds: string[],
  includeCustomEmbedChildren = false,
): Array<{ id: string; label: string; kind: string }> {
  const topLevelSections = parentPool
    .filter(s => s.id !== section.id && linkableKinds.includes(s.kind))
    .map(s => ({ id: s.id, label: s.label || s.kind, kind: s.kind }));

  const childSections = includeCustomEmbedChildren
    ? parentPool
        .filter(s => s.kind === 'CustomEmbed')
        .flatMap(s => {
          const children = s.embedContent?.childSections ?? [];
          return children
            .filter(cs => linkableKinds.includes(cs.kind))
            .map(cs => ({
              id: cs.id,
              label: `${s.label || 'Embed'} → ${cs.label || cs.kind}`,
              kind: cs.kind,
            }));
        })
    : [];

  return [...topLevelSections, ...childSections];
}

// ─── Props ───────────────────────────────────────────────────────────────────

export interface FlexibleSectionContentAdapterProps {
  /** The section whose content is being edited */
  section: LayoutSection;
  /** Full sections array (needed to write the whole array on change) */
  sections: LayoutSection[];
  /** Write updated sections array via onChange('content.flexibleLayout.sections', ...) */
  writeSections: (updated: LayoutSection[]) => void;
  /** Current editing locale */
  locale: string;
  /** Base step — provides id, entityId, and other context fields to the proxy step */
  baseStep: FlowStepNode;
  /** Disables all inputs while saving */
  disabled?: boolean;
  /** Project ID for upload service (needed by FlexibleMediaSection) */
  projectId?: string;
  /** Onboarding ID for upload service */
  onboardingId?: string;
  /** Version ID for upload service */
  versionId?: string;
  /** Available locales for multi-locale upload dialog */
  availableLocales?: string[];
  /** External sections for parent linking (e.g. main FlexibleLayout sections for sticky container buttons) */
  linkableSections?: LayoutSection[];
}

// ─── Component ───────────────────────────────────────────────────────────────

export const FlexibleSectionContentAdapter: React.FC<FlexibleSectionContentAdapterProps> = ({
  section,
  sections,
  writeSections,
  locale,
  baseStep,
  disabled = false,
  projectId,
  onboardingId,
  versionId,
  availableLocales,
  linkableSections,
}) => {
  const kind = section.kind as string;
  const config = KIND_CONFIG[kind];

  // Presentation profile mode (v2.19.1, articles only): typography style
  // writes are routed to presentationOverrides.{profile} inside the section
  // content. 'base' (the only mode for non-articles) leaves paths untouched.
  const activeProfile = useEditorPresentationProfile();

  // ── Refs for stale-closure prevention ──────────────────────────────────
  // StyledTextField's React.memo (shallowCompareProps) does not check onChange,
  // so when proxyOnChange is recreated with fresh data but value/disabled don't
  // change, StyledTextField keeps the OLD callback. Using refs ensures the
  // callback always reads the latest section/sections data.
  const sectionRef = useRef(section);
  sectionRef.current = section;
  const sectionsRef = useRef(sections);
  sectionsRef.current = sections;

  /**
   * Proxy FlowStepNode — has the section's content mapped to the path the V2
   * editor expects. E.g. section.textContent becomes proxyStep.content.copy.
   */
  const proxyStep = useMemo((): FlowStepNode => {
    if (!config) return baseStep;
    return {
      ...baseStep,
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      screenType: config.proxyScreenType as any,
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      content: config.buildProxyContent(section) as any,
    };
  }, [section, baseStep, config]);

  /**
   * Proxy onChange — intercepts V2 editor's path-based updates, translates the path
   * to the section's content field, and writes the whole sections array.
   *
   * Uses refs (sectionRef, sectionsRef) instead of closure-captured values to
   * prevent stale closure bugs when React.memo skips re-render of downstream
   * components (e.g. StyledTextField ignores onChange in its comparator).
   *
   * Example flow (Text section):
   *   path = 'content.copy.title.translations.en', value = 'Hello'
   *   → strips 'content.' → 'copy.title.translations.en'
   *   → prefix 'copy' matches → innerPath = 'title.translations.en'
   *   → deep-clones textContent, applies setNestedValue, updates section, writes array
   */
  const proxyOnChange = useCallback(
    (path: string, value: unknown) => {
      if (!config) return;

      const currentSection = sectionRef.current;
      const currentSections = sectionsRef.current;

      const contentPath = path.startsWith('content.') ? path.slice('content.'.length) : path;
      const prefix = config.onChangePrefixInContent;

      let innerPath: string;
      if (contentPath === prefix) {
        innerPath = ''; // Replacing the entire content field
      } else if (contentPath.startsWith(prefix + '.')) {
        innerPath = contentPath.slice(prefix.length + 1);
      } else {
        return; // Unrelated path — ignore
      }

      const updatedSection: LayoutSection = { ...currentSection };

      if (innerPath === '') {
        // Full field replacement (e.g. enabling/disabling Media section)
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        (updatedSection as any)[config.sectionField] = value;
      } else {
        // Deep update within the section's content field.
        // Deep clone is essential: setNestedValue navigates by string keys so array indices
        // like 'items.0.title' work on existing arrays — but only if the array is a fresh
        // copy (no shared reference mutation).
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const fieldCopy = JSON.parse(JSON.stringify((currentSection as any)[config.sectionField] ?? {}));
        const routedPath = routeTypographyOverridePath(innerPath, activeProfile);
        if (routedPath !== innerPath && (value === null || value === undefined)) {
          // Clearing an override removes the key (keeps overrides sparse)
          deleteNestedValue(fieldCopy as Record<string, unknown>, routedPath);
          pruneEmptyPresentationOverrides(fieldCopy as Record<string, unknown>, routedPath);
        } else {
          setNestedValue(fieldCopy as Record<string, unknown>, routedPath, value);
        }
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        (updatedSection as any)[config.sectionField] = fieldCopy;
      }

      const updatedSections = currentSections.map((s) => (s.id === currentSection.id ? updatedSection : s));
      writeSections(updatedSections);
    },
    [writeSections, config, activeProfile],
  );

  if (!config) return null;

  switch (kind as SectionKind) {
    case 'Text': {
      const parentPool = linkableSections ?? sections;
      const availableParentSections = buildAvailableParentSections(parentPool, section, ['ProductCatalog'], true);
      const handleParentSectionChange = (parentId: string | undefined) => {
        const currentSections = sectionsRef.current;
        const updatedSections = currentSections.map(s =>
          s.id === section.id ? { ...s, parentSectionId: parentId } : s
        );
        writeSections(updatedSections);
      };

      return (
        <FlexibleTextSection
          step={proxyStep}
          locale={locale}
          onChange={proxyOnChange}
          disabled={disabled}
          defaultExpanded={true}

          screenType={baseStep.screenType}
          parentSectionId={section.parentSectionId}
          availableParentSections={availableParentSections}
          onParentSectionChange={handleParentSectionChange}
        />
      );
    }

    case 'Media':
      return (
        <FlexibleMediaSection
          step={proxyStep}
          locale={locale}
          onChange={proxyOnChange}
          disabled={disabled}
          defaultExpanded={true}
          projectId={projectId}
          onboardingId={onboardingId}
          versionId={versionId}
          availableLocales={availableLocales}

        />
      );

    case 'OptionsList':
      return (
        <V2OptionsListSection
          step={proxyStep}
          locale={locale}
          onChange={proxyOnChange}
          disabled={disabled}
          defaultExpanded={true}

        />
      );

    case 'SingleSelectionList':
      return (
        <V2OptionsListSection
          step={proxyStep}
          locale={locale}
          onChange={proxyOnChange}
          disabled={disabled}
          defaultExpanded={true}
          lockedSelectionMode="one"

        />
      );

    case 'MultipleSelectionList':
      return (
        <V2OptionsListSection
          step={proxyStep}
          locale={locale}
          onChange={proxyOnChange}
          disabled={disabled}
          defaultExpanded={true}
          lockedSelectionMode="many"

        />
      );

    case 'BulletList':
      return (
        <V2OptionsListSection
          step={proxyStep}
          locale={locale}
          onChange={proxyOnChange}
          disabled={disabled}
          defaultExpanded={true}
          lockedSelectionMode="browse"

        />
      );

    case 'CustomEmbed':
      return (
        <>
          <V2EmbedSection
            step={proxyStep}
            locale={locale}
            onChange={proxyOnChange}
            disabled={disabled}
            defaultExpanded={true}
            hideDataSources
          />
          <EmbedChildSectionsManager
            section={section}
            sections={sections}
            writeSections={writeSections}
            locale={locale}
            baseStep={baseStep}
            disabled={disabled}
            projectId={projectId}
            onboardingId={onboardingId}
            versionId={versionId}
            showVariableBinding
          />
        </>
      );

    case 'ProductCatalog':
      return (
        <V2PaywallSubscriptionsSection
          step={proxyStep}
          locale={locale}
          onChange={proxyOnChange}
          disabled={disabled}
          defaultExpanded={true}
          projectId={projectId}
          pickerPersistenceScope={`${baseStep.id}:${section.id}:ProductCatalog`}

        />
      );

    case 'PaywallHeader':
      return (
        <V2PaywallHeaderSection
          step={proxyStep}
          locale={locale}
          onChange={proxyOnChange}
          disabled={disabled}
          defaultExpanded={true}

        />
      );

    case 'TextField':
      return (
        <V2TextFieldSection
          step={proxyStep}
          locale={locale}
          onChange={proxyOnChange}
          disabled={disabled}
          defaultExpanded={true}
          projectId={projectId}
          onboardingId={onboardingId}
          versionId={versionId}
        />
      );

    case 'Button': {
      const isPurchase = isPurchaseButton(section);
      const LINKABLE_KINDS = isPurchase
        ? ['ProductCatalog']
        : ['OptionsList', 'SingleSelectionList', 'MultipleSelectionList', 'CustomEmbed', 'TextField'];
      const parentPool = linkableSections ?? sections;
      const availableParentSections = buildAvailableParentSections(parentPool, section, LINKABLE_KINDS, isPurchase);

      const handleParentSectionChange = (parentId: string | undefined) => {
        const currentSections = sectionsRef.current;
        const updatedSections = currentSections.map(s =>
          s.id === section.id ? { ...s, parentSectionId: parentId } : s
        );
        writeSections(updatedSections);
      };

      if (isPurchaseButton(section)) {
        return (
          <FlexiblePurchaseButtonSection
            step={proxyStep}
            locale={locale}
            onChange={proxyOnChange}
            disabled={disabled}
            defaultExpanded={true}
  
            parentSectionId={section.parentSectionId}
            availableParentSections={availableParentSections}
            onParentSectionChange={handleParentSectionChange}
            projectId={projectId}
            onboardingId={onboardingId}
            versionId={versionId}
          />
        );
      }
      return (
        <FlexibleButtonSection
          step={proxyStep}
          locale={locale}
          onChange={proxyOnChange}
          disabled={disabled}
          defaultExpanded={true}

          parentSectionId={section.parentSectionId}
          availableParentSections={availableParentSections}
          onParentSectionChange={handleParentSectionChange}
          projectId={projectId}
          onboardingId={onboardingId}
          versionId={versionId}
        />
      );
    }

    default:
      return null;
  }
};
